import { NavLink, useLocation, useNavigate, Link } from 'react-router-dom';
import { useSidebar } from '@/App';
import {
  LayoutDashboard,
  Briefcase,
  PlusCircle,
  Trash2,
  MessageSquare,
  MessageCircle,
  FolderKanban,
  FileText,
  ChevronLeft,
  ChevronRight,
  LogOut,
  ListChecks,
  Users,
  Building2,
  Palette,
  LayoutGrid,
  BarChart3
} from 'lucide-react';
import { cn } from '@/lib/utils';

const menuItems = [
  { path: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/admin/all-jobs', icon: ListChecks, label: 'All Jobs' },
  { path: '/admin/applied-jobs', icon: Briefcase, label: 'Applied Jobs' },
  { path: '/admin/add-job', icon: PlusCircle, label: 'Add New Jobs' },
  { path: '/admin/removed-jobs', icon: Trash2, label: 'Removed Jobs' },
  { path: '/admin/enquiries', icon: MessageSquare, label: 'Enquiry' },
  { path: '/admin/live-chat', icon: MessageCircle, label: 'Live Chat', highlight: true },
  { path: '/admin/analytics', icon: BarChart3, label: 'Analytics', highlight: true },
  { path: '/admin/talents', icon: Users, label: 'AI Talents' },
  { path: '/admin/projects', icon: FolderKanban, label: 'Projects' },
  { path: '/admin/blogs', icon: FileText, label: 'Blogs' },
  { path: '/admin/clients', icon: Building2, label: 'Client Logos' },
  { path: '/admin/branding', icon: Palette, label: 'White-Label' },
];

export default function Sidebar() {
  const { sidebarOpen, setSidebarOpen, isMobile } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();

  // Check if user has multiple products
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const userProducts = userData.products || [];
  const hasMultipleProducts = userProducts.length > 1 || userData.role === 'super_admin';

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    navigate('/login');
  };

  return (
    <>
      {/* Mobile overlay */}
      {isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed left-0 top-0 h-full bg-slate-900 text-white z-50 transition-all duration-300 flex flex-col",
          sidebarOpen ? "w-64" : "w-20",
          isMobile && !sidebarOpen && "-translate-x-full"
        )}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-700">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_aurous-ecosystem/artifacts/x28qd6xo_30882183-9DD0-40B4-98FF-3BB81A460020.png" 
              alt="Aurous" 
              className="h-8 w-auto"
            />
            {sidebarOpen && (
              <span className="text-[10px] text-blue-400 tracking-wider animate-fade-in">
                ADMIN PORTAL
              </span>
            )}
          </Link>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-1.5 rounded-lg hover:bg-slate-800 transition-colors hidden lg:flex"
            data-testid="sidebar-toggle"
          >
            {sidebarOpen ? (
              <ChevronLeft className="w-5 h-5" />
            ) : (
              <ChevronRight className="w-5 h-5" />
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {/* All Products Link - shown for users with multiple modules */}
          {hasMultipleProducts && (
            <NavLink
              to="/portal"
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all mb-2",
                "text-purple-400 hover:text-purple-300 hover:bg-purple-900/30 border border-purple-500/30"
              )}
              data-testid="nav-all-products"
            >
              <LayoutGrid className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && (
                <span className="animate-fade-in truncate">All Products</span>
              )}
            </NavLink>
          )}
          
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || 
              (item.path !== '/' && location.pathname.startsWith(item.path));
            
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={cn(
                  "sidebar-item flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all",
                  isActive
                    ? "active bg-blue-600/20 text-blue-400"
                    : item.highlight
                    ? "text-green-400 hover:text-green-300 hover:bg-green-900/30"
                    : "text-slate-400 hover:text-white hover:bg-slate-800"
                )}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {sidebarOpen && (
                  <span className="animate-fade-in truncate">{item.label}</span>
                )}
                {item.highlight && sidebarOpen && (
                  <span className="ml-auto px-1.5 py-0.5 text-[10px] bg-green-500 text-white rounded-full font-medium">
                    NEW
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="p-3 border-t border-slate-700">
          <button
            onClick={handleLogout}
            className={cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-lg w-full",
              "text-slate-400 hover:text-red-400 hover:bg-slate-800 transition-all"
            )}
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span className="animate-fade-in">Logout</span>}
          </button>
        </div>
      </aside>
    </>
  );
}
